import queryString from 'query-string';
import http from '../httpConfig';
import compilerHttp from '../httpConfig/compiler';
import { API_ENDPOINTS } from '../utils/apiConstant';
import { ListStock } from '../interfaces/monthlyStock.interface';
import { buildPagingResponse } from '../utils/methods';



// ─── Upload ───────────────────────────────────────────────────────────────────

const upload = (payload: FormData) => {
  return http.post(API_ENDPOINTS.uploadMonthlyStock, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// ─── Compiler (Extract) ───────────────────────────────────────────────────────

const extract = (payload: FormData) => {
  return compilerHttp.post(API_ENDPOINTS.extractMonthlyStock, payload);
};

// ─── List ─────────────────────────────────────────────────────────────────────

const list = (payload: ListStock) => {
  const { searchText, month, year, stockist, branch, user, status, ...others } = payload;
  const paging = buildPagingResponse(others);
  const queryParams: any = {
    ...paging,
    search: searchText || undefined,
    month: month || undefined,
    year: year ? Number(year) : undefined,
    stockist_id: stockist || undefined,
    stockist_branch_id: branch || undefined,
    user_id: user || undefined,
    status: status || undefined,
  };
  const url = `${API_ENDPOINTS.listMonthlyStock}?${queryString.stringify(queryParams)}`;
  return http.get(url);
};

// ─── Lookup: Stockist ─────────────────────────────────────────────────────────

const lookupStockist = (payload: { searchText: string }) => {
  return http.get(API_ENDPOINTS.lookupStockists, { params: { search: payload.searchText } });
};

// ─── Lookup: Branch ───────────────────────────────────────────────────────────

const lookupBranch = (payload: { searchText: string; stockistId?: string | number }) => {
  if (!payload.stockistId) return Promise.resolve({ data: { data: [] } });
  return http.get(API_ENDPOINTS.lookupStockistBranches, {
    params: { search: payload.searchText, stockist_id: payload.stockistId },
  });
};

// ─── Lookup: User ─────────────────────────────────────────────────────────────

const lookupUser = (payload: { searchText: string }) => {
  return http.get(API_ENDPOINTS.lookupUsers, { params: { search: payload.searchText } });
};

// ─── Upload Summary ───────────────────────────────────────────────────────────

const getSummary = (payload: { id: number }) => {
  return http.get(`${API_ENDPOINTS.getMonthlyStockSummary}/${payload.id}/summary`);
};

// ─── Upload Details ───────────────────────────────────────────────────────────

const getDetails = (payload: { id: number; page: number; perPage: number; orderBy: string; order: string }) => {
  const queryParams: any = {
    page: payload.page,
    per_page: payload.perPage,
    order_by: payload.orderBy || 'stockist_product',
    order: payload.order || 'ASC',
  };
  return http.get(`${API_ENDPOINTS.getMonthlyStockDetails}/${payload.id}/details?${queryString.stringify(queryParams)}`);
};

// ─── Verify Data (grouped by division) ───────────────────────────────────────

const getVerifyData = (payload: { id: number }) => {
  return http.get(`${API_ENDPOINTS.getMonthlyStockVerifyData}/${payload.id}/verify-data`);
};

const getDownloadUrl = (payload: { id: number }) => {
  return http.get(`${API_ENDPOINTS.getMonthlyStockDownloadUrl}/${payload.id}/download-url`);
};

// ─── Map Product ──────────────────────────────────────────────────────────────

const mapStockistProduct = (monthlyStockId: number, payload: { stockist_product_id: number; product_id: number; force?: boolean }) => {
  return http.post(`${API_ENDPOINTS.mapStockistProduct}/${monthlyStockId}/map-product`, payload);
};

const submitStatement = (monthlyStockId: number) => {
  return http.post(`${API_ENDPOINTS.submitMonthlyStock}/${monthlyStockId}/submit`);
};

const updateStatus = (monthlyStockId: number, status: string) => {
  return http.patch(`${API_ENDPOINTS.updateMonthlyStockStatus}/${monthlyStockId}/status`, { status });
};

// ─── Export ───────────────────────────────────────────────────────────────────

const MonthlyStockService = {
  upload,
  extract,
  list,
  getSummary,
  getDetails,
  getVerifyData,
  getDownloadUrl,
  lookupStockist,
  lookupBranch,
  lookupUser,
  mapStockistProduct,
  submitStatement,
  updateStatus,
};

export default MonthlyStockService;
